import { datadogLogs, type LogsEvent } from '@datadog/browser-logs'
import type { RuntimeConfig } from './runtime-config'

export type TelemetryContext = Record<string, string | number | boolean>

type TelemetryConfig = Pick<RuntimeConfig, 'datadogClientToken' | 'environment' | 'version'>

let initialized = false

export function initTelemetry({ datadogClientToken, environment, version }: TelemetryConfig): void {
  if (initialized || !datadogClientToken) return
  initialized = true
  datadogLogs.init({
    clientToken: datadogClientToken,
    site: 'datadoghq.com',
    service: 'faceheart-web',
    env: environment,
    version,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    sessionSampleRate: 100,
    telemetrySampleRate: 0,
    beforeSend: scrubEvent,
  })
}

export function reportError(error: unknown, context?: TelemetryContext): void {
  const cause = error instanceof Error ? error : new Error(String(error))
  datadogLogs.logger.error(cause.message, context, cause)
}

const REDACTED = '[redacted]'

const PATHS_WITHOUT_CREDENTIALS = new Set(['', 'assets', 'sdk', 'v2'])

export function scrubUrl(url: string, pageOrigin: string = location.origin): string {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return REDACTED
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return `${parsed.protocol}${REDACTED}`
  }
  return `${parsed.origin}${parsed.origin === pageOrigin ? pagePath(parsed.pathname) : parsed.pathname}`
}

function pagePath(pathname: string): string {
  const first = pathname.split('/')[1] ?? ''
  return PATHS_WITHOUT_CREDENTIALS.has(first) || first.includes('.') ? pathname : '/:token'
}

const URL_IN_TEXT = /(https?:\/\/[^\s"'()<>]+)/

const SENSITIVE_IN_TEXT = [
  /\bBearer\s+[\w.~+/=-]+/gi,
  /\beyJ[\w-]*\.[\w-]+\.[\w-]*/g,
  /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g,
  /\bpub[0-9a-f]{32}\b/gi,
  /\b[0-9a-f]{32,}\b/gi,
  /\b\d{4}-\d{1,2}-\d{1,2}\b/g,
  /\b\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}\b/g,
  /\+?\(?\d[\d\s().-]{7,}\d/g,
  /\d{6,}/g,
]

export function scrubText(text: string): string {
  return text
    .split(URL_IN_TEXT)
    .map((part, index) =>
      index % 2 === 1
        ? scrubUrl(part)
        : SENSITIVE_IN_TEXT.reduce((scrubbed, pattern) => scrubbed.replace(pattern, REDACTED), part),
    )
    .join('')
}

export function scrubEvent(event: LogsEvent): boolean {
  event.message = scrubText(event.message)
  event.view.url = scrubUrl(event.view.url)
  if (event.view.referrer) {
    event.view.referrer = scrubUrl(event.view.referrer)
  }
  if (event.http) {
    event.http.url = scrubUrl(event.http.url)
  }
  if (event.error) {
    if (event.error.message) event.error.message = scrubText(event.error.message)
    if (event.error.stack) event.error.stack = scrubText(event.error.stack)
    for (const cause of event.error.causes ?? []) {
      cause.message = scrubText(cause.message)
      if (cause.stack) cause.stack = scrubText(cause.stack)
    }
  }
  return true
}
